import { memo, useCallback, useMemo, useRef, useState } from "react";
import { View, Text, Card, Button } from "@src/components";
import { Checkbox } from "@src/components/Checkbox";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { router, Stack, useLocalSearchParams } from "expo-router";
import { ActivityIndicator, ScrollView } from "react-native";
import { PressableScale } from "pressto";
import { FloatingSelectionBar } from "@src/features/settings/components/FloatingSelectionBar";
import { AssignSiteRolesBottomSheet } from "@src/features/settings/components/AssignSiteRolesBottomSheet";
import { AssignSiteRolesList, type SiteRole } from "@src/features/settings/components/AssignSiteRolesList";
import type { BottomSheetModal } from "@gorhom/bottom-sheet";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { showSuccessToast, showErrorToast, showInfoToast } from "@src/utils/toast";
import {
  useAccessManagement,
  useCreateRoleAssignment,
  useDeleteCameraPermission,
  type AccessManagementRoleAssignment,
  type DeleteCameraPermissionRequest,
} from "@visioni/a3s-access/app";
import { useCameras } from "@src/features/cameras";
import { buildSiteAccessList } from "@src/features/settings/utils";
import { WebLayout } from "@src/components/web/WebLayout.web";
import { isWeb } from "@src/utils/web-helpers";
// @ts-expect-error baseline 2026-02-16
import { RightDrawer } from "@src/components/web";

const AssignSitesScreen = memo(() => {
  const { theme } = useUnistyles();
  const { userId } = useLocalSearchParams<{ userId: string }>();
  const { bottom } = useSafeAreaInsets();
  const assignRolesRef = useRef<BottomSheetModal>(null);

  const { data, isLoading } = useAccessManagement();
  const { data: cameras } = useCameras();
  const createRoleAssignment = useCreateRoleAssignment();
  const deleteCameraPermission = useDeleteCameraPermission();

  const sites = useMemo(() => (data ? buildSiteAccessList(data, cameras) : []), [data, cameras]);

  const user = useMemo(() => sites.flatMap((s) => s.users).find((u) => u.id === userId), [sites, userId]);

  const userSiteIds = useMemo(
    () => new Set(sites.filter((s) => s.users.some((u) => u.id === userId)).map((s) => s.id)),
    [sites, userId],
  );

  // Camera names grouped by site id
  const cameraNamesBySite = useMemo(() => {
    const map = new Map<string, string[]>();
    if (cameras) {
      for (const camera of cameras) {
        const siteId = camera.site.id;
        if (!map.has(siteId)) map.set(siteId, []);
        map.get(siteId)!.push(camera.name ?? camera.key.asset);
      }
    }
    return map;
  }, [cameras]);

  // Track only newly added site selections — userSiteIds are always checked
  const [addedSiteIds, setAddedSiteIds] = useState<Set<string>>(new Set());

  const selectedSiteIds = useMemo(() => new Set([...userSiteIds, ...addedSiteIds]), [userSiteIds, addedSiteIds]);

  const handleToggleSite = useCallback(
    (siteId: string, checked: boolean) => {
      if (!checked && userSiteIds.has(siteId)) {
        showInfoToast({ message: "To remove a site, use the delete button on the user's detail screen" });
        return;
      }
      setAddedSiteIds((prev) => {
        const next = new Set(prev);
        if (checked) {
          next.add(siteId);
        } else {
          next.delete(siteId);
        }
        return next;
      });
    },
    [userSiteIds],
  );

  const newlySelectedSites = useMemo(
    () =>
      [...addedSiteIds].map((id) => {
        const site = sites.find((s) => s.id === id);
        return {
          siteId: id,
          siteName: site?.name ?? id,
          cameraNames: cameraNamesBySite.get(id) ?? [],
        };
      }),
    [addedSiteIds, sites, cameraNamesBySite],
  );

  // Web-only: RightDrawer state for the "assign roles" step (on native the bottom sheet is used instead).
  const [isAssignRolesDrawerVisible, setIsAssignRolesDrawerVisible] = useState(false);
  const [webRoleMap, setWebRoleMap] = useState<Map<string, SiteRole>>(new Map());

  const handleCloseAssignRolesDrawer = useCallback(() => {
    setIsAssignRolesDrawerVisible(false);
  }, []);

  const handleWebRoleChange = useCallback((siteId: string, role: SiteRole) => {
    setWebRoleMap((prev) => {
      const next = new Map(prev);
      next.set(siteId, role);
      return next;
    });
  }, []);

  const handleAddToSites = useCallback(() => {
    if (isWeb) {
      setWebRoleMap(new Map(newlySelectedSites.map((s) => [s.siteId, "viewer" as SiteRole])));
      setIsAssignRolesDrawerVisible(true);
      return;
    }
    assignRolesRef.current?.present();
  }, [newlySelectedSites]);

  const handleSaveRoles = useCallback(
    async (assignments: { siteId: string; role: string }[]) => {
      try {
        await Promise.all(
          assignments.map(async (assignment) => {
            await createRoleAssignment.mutateAsync({
              user: userId,
              role:
                assignment.role === "manager" ? `site_manager:${assignment.siteId}` : `site_user:${assignment.siteId}`,
            } as AccessManagementRoleAssignment);

            // Site-wide access supersedes per-camera grants on cameras that belong to this site,
            // so revoke any existing ones to keep the permission graph clean.
            const redundantCameraGrants =
              data?.cameras.filter((c) => String(c.user) === userId && c.camera.startsWith(`${assignment.siteId}/`)) ??
              [];
            await Promise.all(
              redundantCameraGrants.map((c) =>
                deleteCameraPermission.mutateAsync({
                  user: userId,
                  camera: c.camera,
                } as DeleteCameraPermissionRequest),
              ),
            );
          }),
        );
        showSuccessToast({ message: "Access updated" });
      } catch (error) {
        showErrorToast({ message: "Failed to update access" });
        throw error;
      }
    },
    [userId, createRoleAssignment, deleteCameraPermission, data],
  );

  const handleWebSaveRoles = useCallback(async () => {
    const assignments = newlySelectedSites.map((site) => ({
      siteId: site.siteId,
      role: webRoleMap.get(site.siteId) ?? ("viewer" as SiteRole),
    }));

    try {
      await handleSaveRoles(assignments);
    } catch {
      return;
    }

    setIsAssignRolesDrawerVisible(false);
    setTimeout(() => {
      router.dismissTo(`/settings/site-access/camera-access/${encodeURIComponent(userId ?? "")}`);
    }, 300);
  }, [newlySelectedSites, webRoleMap, handleSaveRoles, userId]);

  const sitesWithCameras = useMemo(
    () =>
      sites.map((site) => ({
        ...site,
        cameras: cameraNamesBySite.get(site.id) ?? [],
      })),
    [sites, cameraNamesBySite],
  );

  if (isLoading) {
    if (isWeb) {
      return (
        <>
          <Stack.Screen options={{ headerShown: false }} />
          <WebLayout>
            <View style={styles.centered}>
              <ActivityIndicator />
            </View>
          </WebLayout>
        </>
      );
    }
    return (
      <>
        <Stack.Screen
          options={{
            title: "Assign to sites",
            headerBackButtonDisplayMode: "minimal",
            headerStyle: { backgroundColor: theme.colors.background },
          }}
        />
        <View style={styles.centered}>
          <ActivityIndicator />
        </View>
      </>
    );
  }

  if (isWeb) {
    return (
      <>
        <Stack.Screen
          options={{
            headerShown: false,
          }}
        />
        <WebLayout>
          <ScrollView style={styles.scrollView} contentContainerStyle={styles.contentContainerStyle}>
            <Text textStyle="lg/semibold" color="text" mb="3xl">
              Assign sites
            </Text>
            <Card style={{ width: "50%", gap: theme.spacing["2xl"] }}>
              {sitesWithCameras.map((site) => {
                const isSelected = selectedSiteIds.has(site.id);
                return (
                  <PressableScale key={site.id} onPress={() => handleToggleSite(site.id, !isSelected)}>
                    <View style={styles.siteCard}>
                      <View flex={1} gap="xs">
                        <Text textStyle="sm/medium" color="text">
                          {site.name}
                        </Text>
                        {site.cameras.map((cameraName) => (
                          <Text key={cameraName} textStyle="xxs/regular" color="textMuted">
                            {cameraName}
                          </Text>
                        ))}
                      </View>
                      <Checkbox checked={isSelected} onToggle={(checked) => handleToggleSite(site.id, checked)} />
                    </View>
                  </PressableScale>
                );
              })}
            </Card>
          </ScrollView>
        </WebLayout>
        <FloatingSelectionBar
          selectedCount={newlySelectedSites.length}
          actionLabel="Add to Sites"
          onAction={handleAddToSites}
          bottomInset={bottom}
          style={styles.floatingSelectionBar}
        />
        <RightDrawer
          visible={isAssignRolesDrawerVisible}
          onClose={handleCloseAssignRolesDrawer}
          title={user?.name ? `Choose ${user.name}'s roles` : "Choose roles"}
          footer={<Button variant="primary" label="Save" size="md" onPress={handleWebSaveRoles} />}
        >
          <AssignSiteRolesList sites={newlySelectedSites} roleMap={webRoleMap} onRoleChange={handleWebRoleChange} />
        </RightDrawer>
      </>
    );
  }

  return (
    <>
      <Stack.Screen
        options={{
          title: "Assign to sites",
          headerBackButtonDisplayMode: "minimal",
          headerStyle: { backgroundColor: theme.colors.background },
        }}
      />
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.contentContainerStyle}>
        {sitesWithCameras.map((site) => {
          const isSelected = selectedSiteIds.has(site.id);
          return (
            <PressableScale
              key={site.id}
              onPress={() => handleToggleSite(site.id, !isSelected)}
              style={styles.siteCard}
            >
              <View flex={1} gap="xs">
                <Text textStyle="sm/medium" color="text">
                  {site.name}
                </Text>
                {site.cameras.map((cameraName) => (
                  <Text key={cameraName} textStyle="xxs/regular" color="textMuted">
                    {cameraName}
                  </Text>
                ))}
              </View>
              <Checkbox checked={isSelected} onToggle={(checked) => handleToggleSite(site.id, checked)} />
            </PressableScale>
          );
        })}
      </ScrollView>
      <FloatingSelectionBar
        selectedCount={newlySelectedSites.length}
        actionLabel="Add to Sites"
        onAction={handleAddToSites}
        bottomInset={bottom}
      />
      <AssignSiteRolesBottomSheet
        bottomSheetRef={assignRolesRef}
        userId={userId ?? ""}
        userName={user?.name ?? ""}
        sites={newlySelectedSites}
        onSave={handleSaveRoles}
      />
    </>
  );
});

const styles = StyleSheet.create((theme) => ({
  scrollView: {
    flex: 1,
  },
  contentContainerStyle: {
    flexGrow: 1,
    padding: theme.spacing["3xl"],
    gap: theme.spacing.md,
    paddingBottom: 120,
  },
  centered: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  siteCard: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: theme.colors.surfaceCard,
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing["2xl"],
  },
  floatingSelectionBar: {
    width: 350,
    left: "50%",
    right: "auto",
  },
}));

export default AssignSitesScreen;
